import { usePageContext } from "vike-react/usePageContext";
import { useEffect, useState } from "react";
import { MotifWatermark } from "../../components/Motifs.js";
import { langCodeToName } from "../../utils/appTheme.js";

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

export default function Page() {
  const pageContext = usePageContext();
  const langCode = pageContext.routeParams?.language ?? 'es';
  const language = langCodeToName(langCode);
  const [isMobile, setIsMobile] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  async function startCheckout() {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const res = await fetch('/api/billing/checkout', { method: 'POST' });
      const data = await res.json();
      if (data.alreadySubscribed) {
        window.location.href = `/learn-${langNameMap[langCode] ?? 'spanish'}/settings`;
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (res.status === 401) {
        window.location.href = '/api/auth/signin';
        return;
      }
      setCheckoutError('Something went wrong. Please try again.');
    } catch {
      setCheckoutError('Something went wrong. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  }

  const hp = isMobile ? '16px' : '48px';
  const langNameMap: Record<string, string> = { es: 'spanish', pt: 'portuguese', fi: 'finnish' };

  const freeFeatures = [
    '50 verbs per month',
    'Present & Preterite only',
    'Basic progress tracking',
    'Drills mode only',
  ];
  const proFeatures = [
    'Unlimited verbs',
    'All 12 tenses',
    'Flashcards + Sentence builder',
    'Advanced progress analytics',
    'Spanish + Portuguese + Finnish',
    'Priority support',
  ];

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: `36px ${hp}`, background: C.bg, position: 'relative' }}>
      <MotifWatermark language={language} opacity={0.05}/>

      <a href={`/learn-${langNameMap[langCode] ?? 'spanish'}`} style={{
        fontFamily: AF.b, fontSize: 13, color: C.muted, background: 'transparent',
        border: 'none', cursor: 'pointer', marginBottom: 32, padding: 0,
        textDecoration: 'none', display: 'inline-block',
      }}>← Back</a>

      <div style={{ textAlign: 'center', marginBottom: 48, position: 'relative' }}>
        <h1 style={{ fontFamily: AF.h, fontSize: 34, fontWeight: 700, color: C.text, margin: '0 0 12px', letterSpacing: '-1.5px' }}>Simple, honest pricing.</h1>
        <p style={{ fontFamily: AF.b, fontSize: 16, color: C.muted, margin: 0 }}>No streaks to protect. No energy bars. Just practice.</p>
      </div>

      <div style={{ display: 'flex', gap: 20, maxWidth: 700, margin: '0 auto', flexWrap: 'wrap', position: 'relative' }}>
        {/* Free */}
        <div style={{ flex: '1 1 260px', background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12, padding: '28px 26px' }}>
          <div style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6 }}>Free</div>
          <div style={{ fontFamily: AF.h, fontSize: 40, fontWeight: 700, color: C.text, letterSpacing: '-2px', marginBottom: 4 }}>€0</div>
          <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 26 }}>Always free</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 28 }}>
            {freeFeatures.map(f => (
              <div key={f} style={{ display: 'flex', gap: 10 }}>
                <span style={{ color: 'var(--success)', flexShrink: 0 }}>✓</span>
                <span style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>{f}</span>
              </div>
            ))}
          </div>
          <button disabled style={{
            width: '100%', padding: '12px', borderRadius: 7,
            border: `1px solid ${C.border}`, background: 'transparent',
            color: C.muted, fontFamily: AF.b, fontSize: 14, cursor: 'default',
          }}>Current plan</button>
        </div>

        {/* Pro */}
        <div style={{ flex: '1 1 260px', background: C.surf, border: '2px solid var(--accent-border)', borderRadius: 12, padding: '28px 26px', position: 'relative', overflow: 'hidden' }}>
          <MotifWatermark language={language} opacity={0.08}/>
          <div style={{ position: 'absolute', top: 14, right: 14, background: 'var(--accent)', color: C.bg, fontFamily: AF.m, fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 4, letterSpacing: '0.06em', zIndex: 1 }}>POPULAR</div>
          <div style={{ position: 'relative' }}>
            <div style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6 }}>Pro</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: AF.h, fontSize: 40, fontWeight: 700, color: C.text, letterSpacing: '-2px' }}>€8</span>
              <span style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, marginBottom: 8 }}>/month</span>
            </div>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 26 }}>or €64/year — save 33%</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 28 }}>
              {proFeatures.map(f => (
                <div key={f} style={{ display: 'flex', gap: 10 }}>
                  <span style={{ color: 'var(--accent)', flexShrink: 0, fontWeight: 700 }}>✓</span>
                  <span style={{ fontFamily: AF.b, fontSize: 14, color: C.text }}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={startCheckout} disabled={checkoutLoading} style={{
              width: '100%', padding: '12px', borderRadius: 7, border: 'none',
              background: 'var(--accent)', color: C.bg,
              fontFamily: AF.b, fontSize: 14, fontWeight: 700,
              cursor: checkoutLoading ? 'wait' : 'pointer',
              opacity: checkoutLoading ? 0.7 : 1,
            }}>{checkoutLoading ? 'Loading…' : 'Get Pro →'}</button>
            {checkoutError && <p style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--error)', marginTop: 10, textAlign: 'center' }}>{checkoutError}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
